import { useCallback, useState } from "react";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { FaroButton } from "@components/common/faro-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import { assert } from "@faro-lotv/foundation";
import { useToast } from "@hooks/use-toast";
import { publishOrReRegister } from "@pages/data-management/register-data-utils";
import { useAppDispatch } from "@store/store-helper";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends BaseProjectIdProps {
  /** Button caption. */
  caption: string;
  /** ID of the registration revision. */
  registrationId?: string;
  /** Optional callback when the button is clicked, called before the main action. */
  onClickBefore?: () => void;
}

/** Button to retry publishing. */
export function RetryPublishButton({
  projectId,
  caption,
  registrationId,
  onClickBefore,
}: Props): JSX.Element{
  const { trackEvent } = useTrackEvent();
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });
  const registrationApiClient = useRegistrationApiClient({ projectId });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onRetryPublish = useCallback(async(): Promise<void> => {
    assert(registrationId, "Registration ID should be known at this point.");

    setIsLoading(true);
    let hasOperationFailed = false;

    try {
      if (onClickBefore) {
        onClickBefore();
      }
      await publishOrReRegister({
        registrationId,
        progressApiClient,
        projectApiClient,
        registrationApiClient,
        trackEvent,
        showToast,
        dispatch,
        handleErrorWithToast,
      });
    } catch (error) {
      hasOperationFailed = true;
      handleErrorWithToast({
        id: `onRetryPublish-${new Date().toString()}`,
        error,
        title: "Failed to publish the project. Please reload the page and try again.",
      });
    } finally {
      trackEvent({
        name: DataManagementEvents.retryPublish,
        props: {
          hasOperationFailed,
        },
      });
      setIsLoading(false);
    }
  }, [
    dispatch,
    handleErrorWithToast,
    onClickBefore,
    progressApiClient,
    projectApiClient,
    registrationApiClient,
    registrationId,
    showToast,
    trackEvent,
  ]);

  return (
    <SphereTooltip title="Retry to publish the project.">
      <FaroButton
        dataTestId="sa-button-retry-publish"
        isLoading={isLoading}
        onClick={onRetryPublish}
      >
        {caption}
      </FaroButton>
    </SphereTooltip>
  );
}
